import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Paper,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress
} from '@mui/material';
import {
  School,
  AttachMoney,
  CheckCircle,
  Cancel,
  TrendingUp,
  EventNote
} from '@mui/icons-material';
import { usePermissions } from '../hooks/usePermissions';
import { GrilleEmploiSemaine, SeancesDuJour, normaliserRegime } from '../components/EmploiSemaine';

const EtudiantDashboard = () => {
  const { user } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    notes: [],
    moyenne: 0,
    paiements: [],
    totalPaye: 0,
    montantTotal: 0,
    montantNet: 0,
    montantRestant: 0,
    pourcentage_bourse: 0,
    presences: [],
    statsPresences: { total: 0, present: 0, absent: 0, justifiees: 0, nonJustifiees: 0, taux: 0 }
  });
  const [emploi, setEmploi] = useState({ classe: null, classesTA: [], regimes: [], seances: [], total: 0, heures: 0 });
  const [erreurEmploi, setErreurEmploi] = useState('');

  // une entree de presence est une absence quand l'etudiant n'etait pas la
  const absences = (data.presences || []).filter(p => !p.present);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [notesRes, paiementsRes, presencesRes] = await Promise.all([
        axios.get('/api/etudiant/notes', { headers }),
        axios.get('/api/etudiant/paiements', { headers }),
        axios.get('/api/etudiant/presences', { headers })
      ]);

      setData({
        notes: notesRes.data?.notes || [],
        moyenne: notesRes.data?.moyenne || 0,
        paiements: paiementsRes.data?.paiements || [],
        totalPaye: paiementsRes.data?.totalPaye || 0,
        montantTotal: paiementsRes.data?.montantTotal || 0,
        montantNet: paiementsRes.data?.montantNet || 0,
        montantRestant: paiementsRes.data?.montantRestant || 0,
        pourcentage_bourse: paiementsRes.data?.pourcentage_bourse || 0,
        presences: presencesRes.data?.presences || [],
        statsPresences: presencesRes.data?.stats || { total: 0, present: 0, absent: 0, justifiees: 0, nonJustifiees: 0, taux: 0 }
      });
      setError('');

      // l'emploi du temps est optionnel : un backend non encore deploye ne doit pas casser la page
      try {
        const emploiRes = await axios.get('/api/etudiant/emploi', { headers });
        const e = emploiRes.data || {};
        setEmploi({
          classe: e.classe || null,
          classesTA: e.classesTA || [],
          regimes: e.regimes || [],
          seances: e.seances || [],
          total: e.total || 0,
          heures: e.heures || 0
        });
        setErreurEmploi(e.message || (e.classe ? '' : 'Aucune classe trouvée pour votre niveau'));
      } catch (e2) {
        setErreurEmploi('Emploi du temps momentanément indisponible');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de charger vos données');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const seancesNormales = (emploi.seances || []).filter(s => normaliserRegime(s.regime) === 'NORMAL');
  const seancesTA = (emploi.seances || []).filter(s => normaliserRegime(s.regime) === 'TA');
  const aDeuxGrilles = seancesNormales.length > 0 && seancesTA.length > 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        👨‍🎓 Mon Espace Étudiant
      </Typography>

      {/* Cartes */}

      {/* Seances du jour en cours, puis du lendemain apres la fin des cours */}
      {seancesNormales.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, borderLeft: '4px solid #6366f1' }}>
          {aDeuxGrilles && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
              Enseignement normal
            </Typography>
          )}
          <SeancesDuJour seances={emploi.seances} regime="NORMAL" />
        </Paper>
      )}
      {seancesTA.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mb: 3, borderLeft: '4px solid #f59e0b' }}>
          {aDeuxGrilles && (
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
              Temps aménagé (TA)
            </Typography>
          )}
          <SeancesDuJour seances={emploi.seances} regime="TA" />
        </Paper>
      )}

      {/* Emploi du temps */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            🗓️ Mon emploi du temps
          </Typography>
          <Stack direction="row" spacing={1}>
            {emploi.classe && <Chip label={emploi.classe.nom} size="small" sx={{ fontWeight: 600, bgcolor: '#eef2ff', color: '#4f46e5' }} />}
            {emploi.classesTA?.length > 0 && <Chip label={`TA : ${emploi.classesTA.join(', ')}`} size="small" sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }} />}
            <Chip label={`${emploi.total} séance${emploi.total > 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }} />
            <Chip label={`${emploi.heures}h / semaine`} size="small" sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }} />
          </Stack>
        </Box>
        {emploi.seances.length === 0 && (
          <GrilleEmploiSemaine seances={emploi.seances} />
        )}
        {seancesNormales.length > 0 && (
          <Box sx={{ mb: aDeuxGrilles ? 3 : 0 }}>
            {aDeuxGrilles && (
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                Enseignement normal
              </Typography>
            )}
            <GrilleEmploiSemaine seances={emploi.seances} regime="NORMAL" />
          </Box>
        )}
        {seancesTA.length > 0 && (
          <Box>
            {aDeuxGrilles && (
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                Temps aménagé (TA)
              </Typography>
            )}
            <GrilleEmploiSemaine seances={emploi.seances} regime="TA" />
          </Box>
        )}
        {erreurEmploi && (
          <Alert severity="info" sx={{ mt: 2 }}>{erreurEmploi}</Alert>
        )}
      </Paper>

      {/* Notes */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          📊 Mes Notes
        </Typography>
        {data.notes.length > 0 ? (
          <Grid container spacing={2}>
            {data.notes.map((note, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {note.cours?.nom || 'Matière'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Typography variant="h4" fontWeight="bold" color={note.valeur >= 10 ? '#10b981' : '#ef4444'}>
                        {note.valeur}/20
                      </Typography>
                      <Chip
                        label={note.valeur >= 10 ? '✅ Validé' : '❌ Non validé'}
                        size="small"
                        sx={{ bgcolor: note.valeur >= 10 ? '#dcfce7' : '#fee2e2', color: note.valeur >= 10 ? '#166534' : '#991b1b' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {note.session || 'Normale'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="text.secondary">Aucune note enregistrée</Typography>
        )}
      </Paper>

      {/* Absences */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            🚫 Mes Absences
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={`${data.statsPresences.justifiees || 0} justifiée${(data.statsPresences.justifiees || 0) > 1 ? 's' : ''}`}
              size="small" sx={{ fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
            <Chip label={`${data.statsPresences.nonJustifiees || 0} non justifiée${(data.statsPresences.nonJustifiees || 0) > 1 ? 's' : ''}`}
              size="small" sx={{ fontWeight: 600, bgcolor: '#fee2e2', color: '#991b1b' }} />
          </Stack>
        </Box>
        {absences.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Date', 'Cours', 'Statut', 'Retard', 'Justification'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {absences.map(a => (
                  <TableRow key={a._id} hover>
                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {a.date ? new Date(a.date).toLocaleDateString('fr-FR') : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{a.cours_nom || 'Matière'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Chip
                        label={a.justifie ? 'Justifiée' : 'Non justifiée'}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: a.justifie ? '#dcfce7' : '#fee2e2', color: a.justifie ? '#166534' : '#991b1b' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{a.retard ? `${a.retard} min` : '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                      {a.justificationTexte || a.justification || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">Aucune absence enregistrée</Typography>
          </Box>
        )}
      </Paper>

      {/* Paiements réalisés */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            💰 Mes Paiements réalisés
          </Typography>
          <Stack direction="row" spacing={1}>
            {data.pourcentage_bourse > 0 && (
              <Chip label={`Bourse ${data.pourcentage_bourse}%`} size="small" sx={{ fontWeight: 600, bgcolor: '#ede9fe', color: '#5b21b6' }} />
            )}
            <Chip label={`Total ${data.totalPaye.toLocaleString()} DH`} size="small" sx={{ fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }} />
            <Chip label={`Reste ${data.montantRestant.toLocaleString()} DH`} size="small" sx={{ fontWeight: 600, bgcolor: data.montantRestant > 0 ? '#fef3c7' : '#f1f5f9', color: data.montantRestant > 0 ? '#92400e' : '#475569' }} />
          </Stack>
        </Box>
        {data.montantNet > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Avancement du règlement</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669' }}>
                {Math.round((data.totalPaye / data.montantNet) * 100)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={data.montantNet > 0 ? Math.min(100, (data.totalPaye / data.montantNet) * 100) : 0}
              sx={{ height: 8, borderRadius: 99, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { borderRadius: 99, background: '#10b981' } }}
            />
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
              {data.totalPaye.toLocaleString()} DH sur {data.montantNet.toLocaleString()} DH nets
            </Typography>
          </Box>
        )}
        {data.paiements.length > 0 ? (
          <Grid container spacing={2}>
            {data.paiements.map((paiement, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={500}>
                      {paiement.type || 'SCOLARITE'}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="#10b981">
                      {paiement.montant.toLocaleString()} DH
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {paiement.datePaiement ? new Date(paiement.datePaiement).toLocaleDateString('fr-FR') : '-'}
                    </Typography>
                    <Chip
                      label={paiement.statut === 'PAYE' ? '✅ Payé' : '⏳ En attente'}
                      size="small"
                      sx={{ mt: 1, bgcolor: paiement.statut === 'PAYE' ? '#dcfce7' : '#fef3c7', color: paiement.statut === 'PAYE' ? '#166534' : '#92400e' }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="text.secondary">Aucun paiement enregistré</Typography>
        )}
      </Paper>
    </Box>
  );
};

export default EtudiantDashboard;


